/**
 * The view editor's Data Source rows (each an integration and the dimension
 * it is grouped by, or "Platform / Channel Name" for one row per source) and
 * the dimension column: its name and renaming rules ("if it equals X, show
 * Y"), for every source or one, so the same thing lines up across sources.
 */

import type { ReactNode } from 'react';

import type { CatalogueSource } from '../reports/reports-api.ts';
import {
  PLATFORM_DIMENSION,
  type RuleTest,
  type ViewDefinition,
  type ViewRule,
} from './custom-api.ts';

const RULE_TESTS: [RuleTest, string][] = [
  ['EQUALS', 'Equals'],
  ['REGEXP', 'Regexp'],
  ['LT', 'Less Than'],
  ['GT', 'Greater Than'],
  ['LTE', 'Less Than or Equal'],
  ['GTE', 'Greater Than or Equal'],
];

export function newId(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function ViewSources(props: {
  definition: ViewDefinition;
  sources: CatalogueSource[];
  onChange: (definition: ViewDefinition) => void;
}): ReactNode {
  const { definition, sources, onChange } = props;
  const setRows = (rows: ViewDefinition['sources']) => onChange({ ...definition, sources: rows });
  const setRules = (rules: ViewRule[]) =>
    onChange({ ...definition, dimension: { ...definition.dimension, rules } });
  const sourceLabel = (key: string) => sources.find((item) => item.key === key)?.label ?? key;
  return (
    <>
      <section className="cx-section" aria-labelledby="vs-title">
        <h3 id="vs-title">Data Sources</h3>
        {definition.sources.map((row, index) => {
          const source = sources.find((item) => item.key === row.source);
          return (
            <div className="cx-row" key={row.id}>
              <select
                aria-label={`Source ${index + 1}`}
                value={row.source}
                onChange={(event) =>
                  setRows(
                    definition.sources.map((item) =>
                      item.id === row.id
                        ? { ...item, source: event.target.value, dimension: PLATFORM_DIMENSION }
                        : item,
                    ),
                  )
                }
              >
                {sources.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
              <select
                aria-label={`Dimension of source ${index + 1}`}
                value={row.dimension}
                onChange={(event) =>
                  setRows(
                    definition.sources.map((item) =>
                      item.id === row.id ? { ...item, dimension: event.target.value } : item,
                    ),
                  )
                }
              >
                <option value={PLATFORM_DIMENSION}>Platform / Channel Name</option>
                {source?.dimensions
                  .filter((item) => item.key !== 'date')
                  .map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="ex-link"
                onClick={() => setRows(definition.sources.filter((item) => item.id !== row.id))}
              >
                Remove
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="btn secondary"
          disabled={definition.sources.length >= 10 || sources.length === 0}
          onClick={() =>
            setRows([
              ...definition.sources,
              {
                id: newId('s'),
                source: sources[0]?.key ?? '',
                dimension: PLATFORM_DIMENSION,
                filters: [],
              },
            ])
          }
        >
          + Add data source
        </button>
      </section>
      <section className="cx-section" aria-labelledby="vd-title">
        <h3 id="vd-title">Dimension column</h3>
        <label className="rs-field">
          Column Name
          <input
            value={definition.dimension.name}
            maxLength={120}
            onChange={(event) =>
              onChange({
                ...definition,
                dimension: { ...definition.dimension, name: event.target.value },
              })
            }
          />
        </label>
        {definition.dimension.rules.map((rule, index) => {
          const set = (patch: Partial<ViewRule>) =>
            setRules(
              definition.dimension.rules.map((item, at) =>
                at === index ? { ...item, ...patch } : item,
              ),
            );
          return (
            <div className="cx-row cx-rule" key={index}>
              <span>If</span>
              <select
                aria-label="Test"
                value={rule.test}
                onChange={(event) => set({ test: event.target.value as RuleTest })}
              >
                {RULE_TESTS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                aria-label="Value"
                value={rule.value}
                maxLength={100}
                onChange={(event) => set({ value: event.target.value })}
              />
              <span>→</span>
              <input
                aria-label="Show as"
                value={rule.result}
                maxLength={120}
                onChange={(event) => set({ result: event.target.value })}
              />
              <select
                aria-label="Applies to"
                value={rule.source}
                onChange={(event) => set({ source: event.target.value })}
              >
                <option value="ALL">All Data Sources</option>
                {definition.sources.map((row) => (
                  <option key={row.id} value={row.id}>
                    {sourceLabel(row.source)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ex-link"
                onClick={() => setRules(definition.dimension.rules.filter((_, at) => at !== index))}
              >
                Remove
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="btn secondary"
          onClick={() =>
            setRules([
              ...definition.dimension.rules,
              { test: 'EQUALS', value: '', result: '', source: 'ALL' },
            ])
          }
        >
          + Add rule
        </button>
      </section>
    </>
  );
}
